import Decimal from 'decimal.js-light';

import type { TriggerOrderType } from '@/constants/advancedOrder';

const blockThreshold = 0.011;

/**
 * Format the limit rate to at most 255 (uint8) decimals as per the erc20 standard:
 * https://eips.ethereum.org/EIPS/eip-20#decimals.
 */
export const formatLimitRate = (value: string): string => {
  // declare variables
  let formattedRate = '';
  const firstChar = value.charAt(0),
    lastChar = value.charAt(value.length - 1);

  // validate input — guard against non-finite values before reaching the Decimal constructor
  if (value.length === 0 || value === '.') return value;

  if (!Number.isFinite(Number(value))) return '0';

  if (lastChar === '.' || (lastChar === '0' && !(value.includes('e') || value.includes('E'))))
    return value;

  // format output
  formattedRate = new Decimal(value).toDecimalPlaces(255).toString();
  // remove leading zero added by Decimal type conversion
  if (firstChar === '.') formattedRate = formattedRate.slice(1);

  return formattedRate;
};

export type LimitPriceBlockResult = {
  blocked: boolean;
  differencePercent: number;
};

/**
 * Determine whether a limit order should be blocked because it would execute
 * immediately at a loss. An order is blocked when its price is unfavorable
 * (selling below market or buying above market) by at least `blockThreshold`.
 * Prices within the threshold are allowed as a tolerance for price-oracle
 * inaccuracy, and favorable prices are never blocked.
 */
export function computeLimitPriceBlock(
  tokenSide: 'sell' | 'buy',
  limitRate: number,
  marketRate: number,
): LimitPriceBlockResult {
  if (marketRate === 0) return { blocked: false, differencePercent: 0 };

  const differencePercent = (limitRate - marketRate) / marketRate;

  if (tokenSide === 'sell' && limitRate > marketRate) return { blocked: false, differencePercent };
  if (tokenSide === 'buy' && limitRate < marketRate) return { blocked: false, differencePercent };

  if (Math.abs(differencePercent) < blockThreshold) {
    return { blocked: false, differencePercent };
  }

  return { blocked: true, differencePercent };
}

/**
 * Block a Stop-Loss / Take-Profit whose trigger is already through the market.
 * Compares sell-side pair rates (buy-token per 1 sell-token).
 */
export function computeTriggerPriceBlock(
  orderType: TriggerOrderType,
  sellSideTriggerRate: number,
  sellSideMarketRate: number,
): LimitPriceBlockResult {
  if (sellSideMarketRate === 0) return { blocked: false, differencePercent: 0 };

  const differencePercent = (sellSideTriggerRate - sellSideMarketRate) / sellSideMarketRate;
  const wouldFireImmediately =
    orderType === 'stop-loss'
      ? sellSideTriggerRate >= sellSideMarketRate
      : sellSideTriggerRate <= sellSideMarketRate;

  return { blocked: wouldFireImmediately, differencePercent };
}
